import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RotateCcw, Check, Play, User, Stethoscope, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";

const PAY_STEPS = { buy_clarity: 99, buy_blueprint: 279, buy_guided: 749, buy_inhome: 2549 };

const STEP_OUTCOME = {
  timeline: "Saved a 'What happened last night' timeline for the test customer.",
  buy_clarity: "Clarity purchased (TEST). The customer is now directed to the Clarity intake.",
  clarity_intake: "Sample Clarity intake submitted → status 'Awaiting Consultant Review'.",
  clarity_review: "Clarity Report built & published → the customer can now read it in their portal.",
  buy_blueprint: "Blueprint purchased (TEST). The customer is directed to the Blueprint intake.",
  blueprint_intake: "Sample Blueprint intake submitted (reusing Clarity answers) → 'In Preparation'.",
  blueprint_create: "Blueprint built & published → 19-section plan now visible in the portal.",
  buy_guided: "Guided Virtual Consulting purchased (TEST) → Guided support activated.",
  progress: "Sample sleep-log progress added to the Guided workspace.",
  buy_inhome: "In-Home + Overnight purchased (TEST) → onboarding unlocked.",
};

export default function OwnerTest() {
  useSeo({ title: "Test Full Journey", noindex: true });
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const [lastResult, setLastResult] = useState("");

  const load = () => http.get("/owner/test/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { document.title = "Test Full Journey"; load(); }, []);

  const purchasedKeys = new Set((st?.purchases || []).map((p) => p.product_key));
  const reportDone = (p) => (st?.reports || []).some((r) => r.product === p && r.status === "Delivered");
  const svcState = (k) => (st?.services || []).find((s) => s.product_key === k)?.state;
  const stepDone = (key) => ({
    timeline: st?.has_timeline,
    buy_clarity: purchasedKeys.has("diagnostic"),
    clarity_intake: ["Awaiting Operator Review", "Under Review", "Delivered", "Viewed", "Completed"].includes(svcState("diagnostic")),
    clarity_review: reportDone("diagnostic"),
    buy_blueprint: purchasedKeys.has("blueprint"),
    blueprint_intake: ["Awaiting Operator", "In Preparation", "Delivered", "Viewed", "Completed"].includes(svcState("blueprint")),
    blueprint_create: reportDone("blueprint"),
    buy_guided: purchasedKeys.has("guided"),
    progress: false,
    buy_inhome: purchasedKeys.has("inhome_overnight"),
  }[key]);

  const run = async (step) => {
    setBusy(step);
    try {
      const { data } = await http.post("/owner/test/simulate", { step });
      setSt((s) => ({ ...s, ...data }));
      setLastResult(STEP_OUTCOME[step] || "Step simulated.");
      toast.success("Done — see the result below");
    }
    catch (e) { toast.error("Could not simulate step"); } finally { setBusy(""); }
  };
  const reset = async () => {
    setBusy("reset");
    try { await http.post("/owner/test/reset"); await load(); setLastResult("Test customer reset to a clean slate."); toast.success("Test customer reset"); }
    catch (e) { toast.error("Reset failed"); } finally { setBusy(""); }
  };
  const viewAsCustomer = async () => {
    try { await http.post("/owner/impersonate"); window.location.assign("/portal"); }
    catch (e) { toast.error("Could not enter customer test mode."); }
  };

  return (
    <div className="min-h-screen bg-[#161514] text-[#F7F5F0]">
      <div className="max-w-3xl mx-auto px-5 py-10">
        <button onClick={() => nav("/owner")} data-testid="test-back-owner" className="flex items-center gap-1.5 text-sm text-[#A39E98] hover:text-[#F7F5F0] mb-6 transition-colors">
          <ArrowLeft size={15} /> Owner Dashboard
        </button>

        <div className="flex items-center justify-between gap-4 mb-2">
          <h1 className="font-display text-3xl">Test Full Journey</h1>
          <button data-testid="test-reset" onClick={reset} disabled={busy === "reset"}
            className="flex items-center gap-1.5 text-sm border border-[#3a3835] rounded-full px-4 py-2 text-[#A39E98] hover:text-[#F7F5F0] hover:border-[#C2836B]/50 transition-colors disabled:opacity-50">
            <RotateCcw size={14} /> {busy === "reset" ? "Resetting…" : "Reset Test Customer"}
          </button>
        </div>
        <p className="text-[#A39E98] text-sm mb-4">One reusable test customer. Simulated purchases use <span className="text-[#C49A5A]">TEST PAYMENT</span> only and never affect real revenue.</p>

        <div className="rounded-xl border border-[#2E2C2A] bg-[#1C1B1A] p-4 mb-5 text-sm text-[#C6C1B8] leading-relaxed">
          <p className="text-[#F7F5F0] font-medium mb-1">How this works</p>
          Clicking <span className="text-[#C2836B]">Simulate</span> does <span className="text-[#F7F5F0]">not</span> open a new window — it advances the test customer right here. Watch the step turn green ✓ and read the result banner below. Work top to bottom, then click <span className="text-[#C2836B]">View as Customer</span> to see the portal, or <span className="text-[#C2836B]">View as Consultant</span> to build & publish the reports.
        </div>

        {lastResult && (
          <div data-testid="test-last-result" className="rounded-xl border border-[#667A66]/40 bg-[#667A66]/10 p-4 mb-6 text-sm text-[#a9d3a9] flex items-start gap-2">
            <Check size={16} className="mt-0.5 shrink-0" /> <span>{lastResult}</span>
          </div>
        )}

        <div className="flex gap-3 mb-8">
          <button data-testid="test-view-customer" onClick={viewAsCustomer} className="btn-clay rounded-full px-5 py-2.5 text-sm flex items-center gap-2"><User size={15} /> View as Customer</button>
          <button data-testid="test-view-consultant" onClick={() => nav("/operator")} className="btn-outline-night rounded-full px-5 py-2.5 text-sm flex items-center gap-2"><Stethoscope size={15} /> View as Certified Infant Sleep Consultant</button>
        </div>

        <div className="space-y-2" data-testid="test-steps">
          {(st?.steps || []).map(({ key, label }, i) => {
            const done = stepDone(key);
            const pay = PAY_STEPS[key];
            return (
              <div key={key} className={`card-night rounded-xl p-4 flex items-center justify-between gap-3 ${done ? "border-[#667A66]/40" : ""}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-7 h-7 shrink-0 rounded-full grid place-items-center text-xs ${done ? "bg-[#667A66]/20 text-[#8bbf8b]" : "bg-[#1C1B1A] border border-[#3a3835] text-[#A39E98]"}`}>
                    {done ? <Check size={14} /> : i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-[15px] truncate">{label}</p>
                    {pay && <span className="text-[#C49A5A] text-xs flex items-center gap-1"><CreditCard size={11} /> TEST PAYMENT · ${pay}</span>}
                    {done && <p className="text-[#8bbf8b] text-xs mt-1">{STEP_OUTCOME[key]}</p>}
                  </div>
                </div>
                <button data-testid={`test-step-${key}`} onClick={() => run(key)} disabled={busy === key}
                  className="shrink-0 flex items-center gap-1.5 text-sm border border-[#3a3835] rounded-full px-4 py-1.5 text-[#F7F5F0] hover:border-[#C2836B]/60 transition-colors disabled:opacity-50">
                  <Play size={13} /> {busy === key ? "…" : done ? "Re-run" : "Simulate"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
